import { useState } from "react";
import { Sparkles, Loader2, RefreshCw, X } from "lucide-react";
import { toast } from "@/components/ui/sonner";

const API = "";

const PLACEHOLDERS = {
  news: "Décrivez l'actualité en quelques mots… ex : victoire en finale de l'Open Tour samedi, qualification pour les EMEA Masters",
  product: "Décrivez le produit… ex : hoodie noir et orange, coton bio épais, coupe oversize, logo brodé",
  recap: "Ajoutez des précisions (facultatif)… ex : game 3 très serrée, comeback à la 35e minute",
};

/**
 * Panneau « Rédiger avec l'IA » (Claude) pour les formulaires admin.
 * kind: "news" | "product" | "recap" — context: données métier envoyées à l'IA
 * onUse: async ({ title, text }) => void — title non vide uniquement si l'IA en a généré un
 */
export default function AiWriter({ kind, context = {}, onUse, useLabel = "Utiliser ce texte", buttonLabel = "Rédiger avec l'IA", testid }) {
  const [open, setOpen] = useState(false);
  const [notes, setNotes] = useState("");
  const [result, setResult] = useState("");
  const [loading, setLoading] = useState(false);
  const [applying, setApplying] = useState(false);

  const generate = async () => {
    setLoading(true);
    setResult("");
    try {
      const res = await fetch(`${API}/api/admin/ai/generate`, {
        method: "POST",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ kind, prompt: notes, context }),
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.detail || `Erreur ${res.status}`);
      }
      const reader = res.body.getReader();
      const decoder = new TextDecoder();
      let buffer = "";
      for (;;) {
        const { done, value } = await reader.read();
        if (done) break;
        buffer += decoder.decode(value, { stream: true });
        const events = buffer.split("\n\n");
        buffer = events.pop();
        for (const evt of events) {
          const line = evt.replace(/^data: /, "");
          if (line === "[DONE]" || !line) continue;
          try {
            const parsed = JSON.parse(line);
            if (parsed.error) throw new Error(parsed.error);
            if (parsed.text) setResult((prev) => prev + parsed.text);
          } catch (e) {
            if (e instanceof SyntaxError) continue;
            throw e;
          }
        }
      }
    } catch (e) {
      toast.error(e.message || "La génération a échoué");
    } finally {
      setLoading(false);
    }
  };

  const apply = async () => {
    const m = result.match(/^TITRE\s*:\s*(.+)\n+([\s\S]*)$/);
    const payload = m ? { title: m[1].trim(), text: m[2].trim() } : { title: "", text: result.trim() };
    setApplying(true);
    try {
      await onUse(payload);
      setOpen(false);
      setResult("");
      setNotes("");
    } catch (e) {
      toast.error(e.message || "Impossible d'utiliser ce texte");
    } finally {
      setApplying(false);
    }
  };

  return (
    <>
      <button type="button" onClick={() => setOpen(true)} data-testid={testid}
        className="flex w-fit items-center gap-2 rounded-lg border border-orange-500/40 bg-orange-500/10 px-4 py-2 text-sm font-bold uppercase text-orange-400 transition-colors hover:bg-orange-500/20">
        <Sparkles className="h-4 w-4" /> {buttonLabel}
      </button>

      {open && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 p-4 backdrop-blur-sm"
          data-testid={`${testid}-dialog`} onClick={() => !loading && setOpen(false)}>
          <div className="card-tactical w-full max-w-2xl p-6" onClick={(e) => e.stopPropagation()}>
            <div className="mb-4 flex items-center justify-between">
              <p className="flex items-center gap-2 font-heading text-lg font-bold uppercase">
                <Sparkles className="h-5 w-5 text-orange-500" /> Assistant de rédaction IA
              </p>
              <button onClick={() => setOpen(false)} disabled={loading} data-testid={`${testid}-close`}
                className="rounded-lg border border-slate-600 p-1.5 text-slate-400 hover:text-white disabled:opacity-30">
                <X className="h-4 w-4" />
              </button>
            </div>

            <textarea value={notes} onChange={(e) => setNotes(e.target.value)} rows={3}
              placeholder={PLACEHOLDERS[kind]} data-testid={`${testid}-notes`}
              className="w-full rounded-lg border border-input bg-[#060B18] px-3 py-2 text-sm text-white placeholder:text-slate-500 focus:outline-none focus:ring-2 focus:ring-orange-500" />

            <button onClick={generate} disabled={loading} data-testid={`${testid}-generate`}
              className="mt-3 flex items-center gap-2 rounded-lg bg-orange-500 px-5 py-2.5 text-sm font-bold uppercase text-[#060B18] transition-all hover:bg-orange-400 disabled:opacity-50">
              {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : (result ? <RefreshCw className="h-4 w-4" /> : <Sparkles className="h-4 w-4" />)}
              {loading ? "Claude rédige…" : (result ? "Regénérer" : "Générer")}
            </button>

            {(result || loading) && (
              <>
                <textarea value={result} onChange={(e) => setResult(e.target.value)} rows={10}
                  data-testid={`${testid}-result`}
                  className="mt-4 w-full rounded-lg border border-orange-500/30 bg-[#060B18] px-3 py-2 text-sm leading-relaxed text-white focus:outline-none focus:ring-2 focus:ring-orange-500" />
                <p className="mt-1 text-xs text-slate-500">Vous pouvez retoucher le texte avant de l'utiliser.</p>
                <button onClick={apply} disabled={loading || applying || !result.trim()} data-testid={`${testid}-use`}
                  className="mt-3 rounded-lg bg-emerald-500 px-5 py-2.5 text-sm font-bold uppercase text-[#060B18] transition-all hover:bg-emerald-400 disabled:opacity-50">
                  {applying ? "En cours…" : useLabel}
                </button>
              </>
            )}
          </div>
        </div>
      )}
    </>
  );
}
